/**
 * price-format — splitting a сом price into a FIGURE and a UNIT.
 *
 * Why this exists next to `@/lib/format-currency`:
 *
 * The counter-slip direction sets every price as an instrument reading — the
 * number in IBM Plex Mono with tabular figures (`.g-data`), the unit in body
 * type at meta weight beside it. `formatKgs()` returns one fused string
 * ("29 000 сом"), which forces the unit into the mono face too; a column of
 * prices then reads as "29 000 сом / 5 000 сом" with the unit fighting the
 * figure for the same optical weight. Splitting them is what lets a wall of
 * cards line their сом figures up on one invisible right edge.
 *
 * The number itself is still `Intl.NumberFormat` at the same KGS locale, so
 * the grouping and decimal separators are byte-identical to what `formatKgs`
 * produced ("1 234,56" in ru-KG / ky-KG). Only the currency token is dropped —
 * and it is re-supplied by the caller from the `card.currency` i18n key, which
 * is the product's own spelling of the unit: **сом**, never "KGS"
 * (PRODUCT.md → Terminology).
 *
 * `formatKgs` itself is untouched: chat product cards and the seller surfaces
 * still use it, and this module deliberately does not change their output.
 *
 * No PII travels here — prices are public catalogue data.
 */
import type { KgsLocale } from "@/lib/format-currency";

/**
 * Kyrgyz number formatting, with an explicit fallback.
 *
 * `ky` is not present in every browser's ICU data set — Chromium here resolves
 * `Intl.NumberFormat("ky-KG").resolvedOptions().locale` to **en-US**, which
 * means a Kyrgyz-speaking user was being shown `29,000` with an English comma
 * group separator (and, through `formatKgs`, the literal token "KGS"). Kyrgyz
 * uses the same conventions as Russian — a space between thousands and a comma
 * before the decimals — so when `ky` is unavailable we fall back to `ru-KG`
 * rather than to whatever the runtime happens to pick. A price is the one
 * figure on this surface a buyer must not misread, and `29,000` versus
 * `29 000` is exactly the ambiguity to eliminate.
 *
 * Resolved once per session: `Intl` locale negotiation is not free and the
 * answer cannot change while the page is open.
 */
let kyNumberLocale: string | null = null;

function effectiveLocale(locale: KgsLocale): string {
  if (locale !== "ky-KG") return locale;
  if (kyNumberLocale === null) {
    let resolved = "ru-KG";
    try {
      const actual = new Intl.NumberFormat("ky-KG").resolvedOptions().locale;
      if (actual.toLowerCase().startsWith("ky")) resolved = "ky-KG";
    } catch {
      // Intl unavailable or the tag was rejected — the ru-KG default stands.
    }
    kyNumberLocale = resolved;
  }
  return kyNumberLocale;
}

/**
 * The grouped decimal figure ONLY — no currency token, no unit.
 *
 * Pass the `price_kgs` decimal verbatim (NOT divided by 100; there is no
 * integer-tiyin representation in this codebase — ADR-031). Returns e.g.
 * `"1 234,56"` in ru-KG / ky-KG and `"29 000"` for a whole-сом price.
 */
export function formatPriceFigure(priceKgs: number, locale: KgsLocale): string {
  return new Intl.NumberFormat(effectiveLocale(locale), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(priceKgs);
}

/**
 * Both ends of a price BAND as one figure string — `"5 500 – 14 000"`.
 *
 * Half the catalogue sells in a band rather than at a point: of the 4,545 rows
 * in the seller workbooks, 2,328 carry a range like «250-500». The database has
 * stored both ends since v0048, but every card read `price_kgs` alone — which
 * for a banded product is its LOW end — so a product selling at 5 500–14 000
 * сом was shown as a firm "5 500 сом". That is not a missing price; it is the
 * wrong one, quoted with confidence.
 *
 * Separator is an EN DASH with ordinary spaces, not a hyphen: the figures
 * already contain space group separators ("5 500"), and a hyphen tight against
 * them reads as a minus sign or a part number. The unit stays the caller's
 * job, exactly as with `formatPriceFigure` — one «сом» after the pair, never
 * one after each end.
 */
export function formatPriceRange(
  minKgs: number,
  maxKgs: number,
  locale: KgsLocale,
): string {
  const lo = formatPriceFigure(minKgs, locale);
  const hi = formatPriceFigure(maxKgs, locale);
  return `${lo} – ${hi}`;
}
